import * as Sentry from '@sentry/react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { type ExportPdpsCsvQueryVariables, useExportPdpsCsvQuery } from '@vizit/api';
import type { ListingScoreLikertLevel, PdpViewFields } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { FEATURE_FLAGS, SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS, isOtherRetailerRegion } from '@vizit/constants';
import { useFeatureFlag, useShowUniversalBenchmarks } from '@vizit/feature-flags';
import type { ProductListingData } from '@vizit/molecules';
import { RefreshPDPDialogVariant, ScoreLevel } from '@vizit/organisms';
import * as React from 'react';
import { useSearchPdpsInfiniteQuery } from '../../hooks';
import { transformStateToQueryParams, useProductTable } from '../../providers';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { RefreshProductDialog } from '../refresh-product-dialog';
import { toProductListingRow, triggerCsvDownload } from './utils';

interface UseProductTableViewerResult {
  /**
   * Array of all products from all fetched pages
   *
   * @type {PdpView[]}
   */
  rows: ProductListingData[];
  /**
   * Whether the initial data is loading
   *
   * @type {boolean}
   */
  isLoading: boolean;
  /**
   * Whether there was an error fetching data
   *
   * @type {boolean}
   */
  hasError: boolean;
  /**
   * Whether there are more pages to fetch
   *
   * @type {boolean}
   */
  hasNextPage: boolean;
  /**
   * Whether the next page is currently being fetched
   *
   * @type {boolean}
   */
  isFetchingNextPage: boolean;
  /**
   * Total count of products
   *
   * @type {number}
   */
  totalCount: number;
  /**
   * Function to load the next page of data
   *
   * @type {() => void}
   */

  fetchNextPage: () => Promise<unknown>;
}

/**
 * Hook to fetch portfolio products for the product table viewer with infinite loading
 * Uses the generated getPdps query internally and provides infinite scroll functionality
 *
 * @param props - The hook configuration
 * @param props.workspaceId - The ID of the portfolio to fetch products for
 * @param props.limit - Number of items to fetch per page (default: 20)
 * @param props.sortBy - Sort field for the products (default: 'created_at')
 * @param props.sortDirection - Sort direction for the products (default: 'desc')
 *
 * @returns Object containing product data and control functions
 *
 * @example
 * ```tsx
 * const { products, isLoading, hasNextPage, loadMore, sentinelRef, productImages } = useProductTableViewer({
 *   workspaceId: 'portfolio-123',
 *   organizationId: 'org-123',
 *   limit: 20,
 *   sortBy: 'created_at',
 *   sortDirection: 'desc'
 * });
 * ```
 */
function useProductTableViewer(): UseProductTableViewerResult {
  const {
    organizationId,
    workspaceId,
    limit,
    sortBy,
    sortDirection,
    brand,
    productCategoryId,
    categoryId,
    listingScoreLevel,
    searchTerm,
    retailer,
    experimentOnly,
  } = useProductTable();

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);
  const showUniversalBenchmarks = useShowUniversalBenchmarks();
  // A saved or leftover Other filter is never sent while Other is not offered.
  const retailerFilter =
    isOtherRetailerRegion(retailer) && !(isRetailerEnabled && showUniversalBenchmarks) ? undefined : retailer;

  const filterQueryParams = React.useMemo(() => {
    const base = transformStateToQueryParams({
      limit,
      sortBy,
      sortDirection,
      brand,
      productCategoryId,
      categoryId,
      listingScoreLevel,
      searchTerm,
      retailer: retailerFilter,
    });
    return {
      ...base,
      // Mirror the export hook: when retailer flag is on, suppress the UUID field;
      // when it's off, suppress the retailer string field.
      product_category_id: isRetailerEnabled ? undefined : base.product_category_id,
      category_id: isRetailerEnabled ? base.category_id : undefined,
    };
  }, [
    limit,
    sortBy,
    sortDirection,
    brand,
    productCategoryId,
    categoryId,
    isRetailerEnabled,
    listingScoreLevel,
    searchTerm,
    retailerFilter,
  ]);

  const queryResult = useSearchPdpsInfiniteQuery({
    workspace_id: workspaceId ?? '',
    organization_id: organizationId,
    ...filterQueryParams,
    experimentOnly,
  });
  const { data, isLoading, isRefetching, error, hasNextPage, isFetchingNextPage, fetchNextPage } = queryResult;

  const products = React.useMemo(() => {
    const uniqueProductIds = new Set<string>();
    return (
      data?.pages
        ?.flatMap((page) => page.data.products)
        ?.filter((p) => {
          if (uniqueProductIds.has(p.id)) {
            return false;
          }
          uniqueProductIds.add(p.id);
          return true;
        }) || []
    );
  }, [data]);

  const rows = React.useMemo(() => products.map(toProductListingRow), [products]);

  const totalCount = React.useMemo(() => data?.pages?.[0]?.data.totalCount ?? 0, [data]);

  return {
    rows,
    isLoading: isLoading || isRefetching, // refetch is triggered when the user changes the filters, so we need to show the loading state until the refetch is complete
    hasError: !!error,
    hasNextPage: hasNextPage || false,
    isFetchingNextPage,
    totalCount,
    fetchNextPage,
  };
}

export { useProductTableViewer };
export type { UseProductTableViewerResult };

interface UseProductTableDownloadCsvResult {
  handleDownloadCsv: () => Promise<void>;
  isDownloadingCsv: boolean;
  shouldShowDownloadButton: boolean;
  isDownloadDisabled: boolean;
}

function useProductTableDownloadCsv(): UseProductTableDownloadCsvResult {
  const {
    workspaceId,
    sortBy = 'created_at',
    sortDirection = 'desc',
    brand,
    productCategoryId,
    categoryId,
    listingScoreLevel,
    searchTerm,
  } = useProductTable();

  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const exportParams = React.useMemo<ExportPdpsCsvQueryVariables>(() => {
    const normalizedSearchTerm = searchTerm?.trim();

    return {
      workspace_id: workspaceId ?? '',
      sort_by: sortBy as PdpViewFields,
      sort_direction: sortDirection,
      brand: brand ? [brand] : undefined,
      product_category_id: isRetailerEnabled || !productCategoryId ? undefined : [productCategoryId],
      category_id: isRetailerEnabled && categoryId ? [categoryId] : undefined,
      listing_score_level:
        listingScoreLevel && listingScoreLevel !== ScoreLevel.All
          ? [listingScoreLevel as ListingScoreLikertLevel]
          : undefined,
      search_name: normalizedSearchTerm,
      search_brand: normalizedSearchTerm,
      search_product_category: normalizedSearchTerm,
      listing_score_at_ingest_level: undefined,
      search_item_id: undefined,
    };
  }, [
    brand,
    categoryId,
    isRetailerEnabled,
    listingScoreLevel,
    workspaceId,
    productCategoryId,
    searchTerm,
    sortBy,
    sortDirection,
  ]);

  const exportQueryKey = React.useMemo(() => useExportPdpsCsvQuery.getKey(exportParams), [exportParams]);
  const exportQuery = React.useMemo(() => useExportPdpsCsvQuery.fetcher(exportParams), [exportParams]);
  const isDownloadingCsv = useIsFetching({ queryKey: exportQueryKey }) > 0;

  const handleDownloadCsv = React.useCallback(async () => {
    if (!workspaceId || isDownloadingCsv) {
      return;
    }

    try {
      const result = await queryClient.fetchQuery({
        queryKey: exportQueryKey,
        queryFn: exportQuery,
        retry: false,
        staleTime: 0,
      });

      if (result instanceof Blob) {
        triggerCsvDownload(result);
        return;
      }

      triggerCsvDownload(result.data, result.content_disposition);
    } catch (error) {
      addToast({
        title: 'Download failed',
        description: 'Unable to download the CSV. Please try again later.',
        level: AlertLevel.Error,
      });
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          feature: SENTRY_FEATURE_TAGS.CsvDownload,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.CsvDownload].products,
        },
        extra: {
          page: 'library products',
        },
      });
    }
  }, [addToast, exportQuery, exportQueryKey, isDownloadingCsv, workspaceId, queryClient]);

  const shouldShowDownloadButton = !!workspaceId;
  const isDownloadDisabled = !workspaceId || isDownloadingCsv;

  return {
    handleDownloadCsv,
    isDownloadingCsv,
    shouldShowDownloadButton,
    isDownloadDisabled,
  };
}

export { useProductTableDownloadCsv };
export type { UseProductTableDownloadCsvResult };

interface UseProductTableRefreshResult {
  handleRefreshProducts: () => void;
  isRefreshing: boolean;
  shouldShowRefreshButton: boolean;
  isRefreshDisabled: boolean;
  refreshDialogElement: React.ReactNode;
}

function useProductTableRefresh(): UseProductTableRefreshResult {
  const { workspaceId } = useProductTable();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [refreshDialogElement, setRefreshDialogElement] = React.useState<React.ReactNode>(null);

  // Auto-clear refresh state after 5 minutes
  React.useEffect(() => {
    if (!isRefreshing) return;

    const timeoutId = setTimeout(
      () => {
        setIsRefreshing(false);
      },
      5 * 60 * 1000,
    ); // 5 minutes

    return () => clearTimeout(timeoutId);
  }, [isRefreshing]);

  const closeDialog = React.useCallback(() => {
    setRefreshDialogElement((prev) => {
      if (prev && React.isValidElement(prev)) {
        return React.cloneElement(prev as React.ReactElement<{ isOpen: boolean }>, { isOpen: false });
      }
      return prev;
    });
    setTimeout(() => setRefreshDialogElement(null), 300);
  }, []);

  const handleRefreshProducts = React.useCallback(() => {
    if (!workspaceId) return;

    const handleOpenChange = (isOpen: boolean) => {
      if (!isOpen) {
        closeDialog();
      }
    };

    const handleRefreshStarted = () => {
      setIsRefreshing(true);
    };

    setRefreshDialogElement(
      <RefreshProductDialog
        workspaceId={workspaceId}
        variant={RefreshPDPDialogVariant.All}
        isOpen={true}
        onOpenChange={handleOpenChange}
        onRefreshStarted={handleRefreshStarted}
      />,
    );
  }, [workspaceId, closeDialog]);

  const shouldShowRefreshButton = !!workspaceId;
  const isRefreshDisabled = !workspaceId;

  return {
    handleRefreshProducts,
    isRefreshing,
    shouldShowRefreshButton,
    isRefreshDisabled,
    refreshDialogElement,
  };
}

export { useProductTableRefresh };
export type { UseProductTableRefreshResult };
